import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { ArrowLeft, ArrowRight, Check, Clock, Users, Star, BookOpen, GraduationCap } from 'lucide-react';

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/courses/${courseId}`)
      .then(res => {
        setCourse(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Course not found');
        setLoading(false);
      });
  }, [courseId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-cyan"></div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center py-16">
        <div className="text-center">
          <h1 className="font-outfit text-3xl font-bold text-white mb-4">Course Not Found</h1>
          <p className="text-slate-400 mb-8">The course you're looking for doesn't exist or has been removed.</p>
          <Link to="/courses" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <button 
            onClick={() => navigate('/courses')}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-courses"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Courses</span>
          </button>
        </motion.div>

        {/* Course Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 md:p-12 mb-8 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-electric-cyan/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {course.is_featured && (
                <div className="inline-flex items-center gap-1 bg-electric-cyan/20 text-electric-cyan text-sm font-medium px-3 py-1 rounded-full">
                  <Star className="w-4 h-4" /> Most Popular
                </div>
              )}
              {course.grade_levels?.map((grade, i) => (
                <span key={i} className="text-sm bg-surface-navy px-3 py-1 rounded-full text-slate-300">{grade}</span>
              ))}
            </div>
            
            <h1 className="font-outfit text-3xl md:text-5xl font-bold text-white mb-4" data-testid="course-title">
              {course.name}
            </h1>
            
            <p className="text-slate-400 text-lg mb-8 max-w-3xl">
              {course.description}
            </p>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-6 mb-8">
              <div className="flex items-center gap-2 text-slate-300">
                <Clock className="w-5 h-5 text-electric-cyan" />
                <span>{course.duration}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <BookOpen className="w-5 h-5 text-electric-cyan" />
                <span>{course.subjects?.length || 0} Subjects</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <GraduationCap className="w-5 h-5 text-electric-cyan" />
                <span>{course.grade_levels?.join(', ') || 'All Levels'}</span>
              </div>
            </div>

            {/* Price & CTA */}
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <span className="text-4xl md:text-5xl font-outfit font-bold text-electric-cyan">SGD {course.price}</span>
                <span className="text-slate-400 ml-2">/ {course.duration}</span>
              </div>
              <Link 
                to="/register" 
                className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
                data-testid="course-enroll-btn"
              >
                Enroll Now <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Course Details Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Subjects */}
          {course.subjects && course.subjects.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6"
            >
              <h2 className="font-outfit text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-electric-cyan" />
                Subjects Covered
              </h2>
              <div className="flex flex-wrap gap-3">
                {course.subjects.map((subject, i) => (
                  <span 
                    key={i} 
                    className="bg-electric-cyan/10 text-electric-cyan px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    {subject}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Features */}
          {course.features && course.features.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6"
            >
              <h2 className="font-outfit text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                <Check className="w-6 h-6 text-electric-cyan" />
                What's Included
              </h2>
              <div className="space-y-4">
                {course.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-electric-cyan/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-electric-cyan" />
                    </div>
                    <span className="text-slate-300">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-8 md:p-12 mt-8 text-center"
        >
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Join hundreds of students who have achieved their academic goals with {course.name}.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/register" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="course-cta-enroll"
            >
              Enroll Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/contact" 
              className="btn-secondary inline-flex items-center gap-2"
            >
              Get Free Consultation
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CourseDetail;
